'use client';

import { useEffect, useRef, useState } from 'react';
import { signIn } from 'next-auth/react';
import { FaYandex } from 'react-icons/fa';

import { PublicLink } from '@/components/PublicLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DeferredTurnstile, type BoundTurnstileObject } from '@/components/DeferredTurnstile';
import { SurfacePanel } from '@/components/site/page-primitives';
import {
  buildPostAuthRedirect,
  type CheckoutPlanSlug,
  withCheckoutPlanLink,
} from '@/lib/auth-intent';
import { waitForAuthenticatedSession } from '@/lib/auth-session-sync';
import { getAuthProviderViaLabel, getTurnstileErrorMessage } from '@/lib/authCopy';
import {
  resolvePublicYandexAuthState,
  signInWithPublicYandex,
} from '@/lib/public-yandex-auth';
import {
  getSignupIntroDescription,
  shouldShowYandexAuthCallToAction,
} from '@/lib/authUi';
import {
  isClientTurnstileBypassEnabled,
  isCloudflareTestingSiteKey,
  resolvePublicTurnstileSiteKey,
} from '@/lib/turnstileConfig';
import { useTurnstileActivation } from '@/hooks/useTurnstileActivation';
import { useToast } from '@/hooks/use-toast';

type SignupField = 'name' | 'email' | 'password' | 'confirmPassword';
type SignupErrors = Partial<Record<SignupField, string>>;

function validateSignupForm({
  name,
  email,
  password,
  confirmPassword,
}: Record<SignupField, string>): SignupErrors {
  const errors: SignupErrors = {};

  if (name.trim().length < 2) {
    errors.name = 'Имя должно содержать минимум 2 символа';
  }

  if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = 'Введите корректный адрес электронной почты';
  }

  if (password.length < 8) {
    errors.password = 'Пароль должен содержать минимум 8 символов';
  }

  if (password !== confirmPassword) {
    errors.confirmPassword = 'Пароли не совпадают';
  }

  return errors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }

  return <p className="text-sm text-destructive">{message}</p>;
}

export function SignupFormClient({
  checkoutPlan,
  customRedirect,
  yandexAuthEnabled,
  yandexClientId,
}: {
  checkoutPlan: CheckoutPlanSlug | null;
  customRedirect?: string | null;
  yandexAuthEnabled: boolean;
  yandexClientId: string | null;
}) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<SignupErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const turnstileRef = useRef<BoundTurnstileObject | null>(null);
  const captchaResolveRef = useRef<((token: string) => void) | null>(null);
  const captchaRejectRef = useRef<((err: Error) => void) | null>(null);
  const captchaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captchaReadyWaiterRef = useRef<{
    resolve: (bound: BoundTurnstileObject) => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
  } | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  const captchaExecutionRef = useRef(false);

  const postAuthRedirect = buildPostAuthRedirect(checkoutPlan, customRedirect);
  const { yandexAuthEntrypointEnabled } = resolvePublicYandexAuthState({
    yandexAuthEnabled,
    yandexClientId,
  });
  const showYandexAuth = shouldShowYandexAuthCallToAction(yandexAuthEntrypointEnabled);

  const turnstileSiteKey = resolvePublicTurnstileSiteKey({
    envValue: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    nodeEnv: process.env.NODE_ENV,
  });
  const usesAlwaysPassKey = isCloudflareTestingSiteKey(turnstileSiteKey);
  const turnstileBypass = isClientTurnstileBypassEnabled(process.env.NEXT_PUBLIC_TURNSTILE_BYPASS);
  const turnstileEnabled = !turnstileBypass && Boolean(turnstileSiteKey);
  const { activateTurnstile, isTurnstileActivated, turnstileActivationProps } =
    useTurnstileActivation(turnstileEnabled);

  const clearCaptchaWaiters = () => {
    if (captchaTimeoutRef.current) {
      clearTimeout(captchaTimeoutRef.current);
      captchaTimeoutRef.current = null;
    }
    captchaResolveRef.current = null;
    captchaRejectRef.current = null;
  };

  const clearCaptchaExecution = () => {
    captchaExecutionRef.current = false;
  };

  const waitForCaptchaReady = async () => {
    if (turnstileBypass) {
      return null;
    }
    if (turnstileRef.current) {
      return turnstileRef.current;
    }

    return new Promise<BoundTurnstileObject>((resolve, reject) => {
      if (captchaReadyWaiterRef.current) {
        clearTimeout(captchaReadyWaiterRef.current.timeout);
      }

      const timeout = setTimeout(() => {
        captchaReadyWaiterRef.current = null;
        reject(new Error('Captcha not ready'));
      }, 15000);

      captchaReadyWaiterRef.current = { resolve, reject, timeout };
    });
  };

  const requestCaptchaToken = async () => {
    if (turnstileBypass) {
      return 'bypass';
    }
    if (captchaTokenRef.current) {
      return captchaTokenRef.current;
    }

    await activateTurnstile();
    const bound = await waitForCaptchaReady();
    if (!bound) {
      return 'bypass';
    }

    return new Promise<string>((resolve, reject) => {
      clearCaptchaWaiters();
      captchaTimeoutRef.current = setTimeout(() => {
        clearCaptchaWaiters();
        clearCaptchaExecution();
        reject(new Error('Captcha timed out'));
      }, 8000);

      captchaResolveRef.current = (token: string) => {
        clearCaptchaWaiters();
        resolve(token);
      };

      captchaRejectRef.current = (error: Error) => {
        clearCaptchaWaiters();
        reject(error instanceof Error ? error : new Error('Captcha failed'));
      };

      try {
        if (captchaTokenRef.current) {
          const existingToken = captchaTokenRef.current;
          clearCaptchaWaiters();
          resolve(existingToken);
          return;
        }

        if (!usesAlwaysPassKey && bound.execute && !captchaExecutionRef.current) {
          captchaExecutionRef.current = true;
          bound.execute();
        }
      } catch (error) {
        clearCaptchaWaiters();
        clearCaptchaExecution();
        reject(error instanceof Error ? error : new Error('Captcha failed'));
      }
    });
  };

  const handleCaptchaVerify = (token: string) => {
    captchaTokenRef.current = token;
    clearCaptchaExecution();
    captchaResolveRef.current?.(token);
  };

  const handleCaptchaError = (error?: unknown) => {
    captchaTokenRef.current = null;
    clearCaptchaExecution();
    const err = error instanceof Error ? error : new Error('Captcha failed');
    captchaRejectRef.current?.(err);
  };

  useEffect(() => {
    captchaTokenRef.current = null;
    clearCaptchaWaiters();
    clearCaptchaExecution();
  }, [turnstileEnabled, usesAlwaysPassKey]);

  const clearFieldError = (field: SignupField) => {
    if (!errors[field]) {
      return;
    }

    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateSignupForm({ name, email, password, confirmPassword });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      let captchaToken: string | undefined;

      if (!turnstileBypass) {
        try {
          captchaToken = await requestCaptchaToken();
        } catch (error) {
          const message = getTurnstileErrorMessage(error);
          toast({
            variant: 'destructive',
            title: 'Не удалось зарегистрироваться',
            description: message,
          });
          turnstileRef.current?.reset();
          captchaTokenRef.current = null;
          return;
        }
      }

      const registerResponse = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password,
          captchaToken,
        }),
      });

      if (!registerResponse.ok) {
        const errorData = (await registerResponse.json().catch(() => null)) as { error?: string } | null;
        throw new Error(errorData?.error || 'Не удалось зарегистрировать пользователя');
      }

      const result = await signIn('credentials', {
        redirect: false,
        email: email.trim(),
        password,
        callbackUrl: postAuthRedirect,
      });

      if (result?.error) {
        throw new Error(result.error);
      }
      if (result?.ok === false) {
        throw new Error('Не удалось завершить вход');
      }

      await waitForAuthenticatedSession();

      toast({
        title: 'Аккаунт создан',
        description: 'Добро пожаловать в S-SHOT.',
      });
      window.location.assign(result?.url ?? postAuthRedirect);
    } catch (error) {
      if (!turnstileBypass) {
        turnstileRef.current?.reset();
        captchaTokenRef.current = null;
      }
      toast({
        variant: 'destructive',
        title: 'Не удалось зарегистрироваться',
        description: error instanceof Error ? error.message : 'Во время создания аккаунта произошла ошибка',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'yandex') => {
    const providerLabel = getAuthProviderViaLabel(provider);

    try {
      setSocialLoading(provider);
      await signInWithPublicYandex({
        callbackUrl: postAuthRedirect,
        yandexAuthEnabled,
        yandexClientId,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Не удалось войти',
        description: error instanceof Error ? error.message : `Не удалось войти через ${providerLabel}`,
      });
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <SurfacePanel className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">Регистрация</h2>
        <p className="mt-2 text-sm text-[var(--site-ink-muted)]">
          {getSignupIntroDescription(checkoutPlan, yandexAuthEntrypointEnabled)}
        </p>
      </div>

      {showYandexAuth ? (
        <>
          <div className="flex flex-col space-y-4">
            <Button
              type="button"
              variant="outline"
              className="flex w-full items-center justify-center gap-2 py-5"
              onClick={() => handleSocialLogin('yandex')}
              disabled={Boolean(socialLoading)}
              data-testid="signup-yandex-button"
            >
              {socialLoading === 'yandex' ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--site-primary-fill)] border-t-transparent" />
              ) : (
                <FaYandex className="text-[#fc3f1d]" />
              )}
              <span>Зарегистрироваться через Yandex</span>
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[color:var(--site-border-soft)]" />
            </div>
            <div className="relative flex justify-center text-sm font-medium">
              <span className="bg-[var(--site-surface-strong)] px-4 text-[var(--site-ink-subtle)]">Или</span>
            </div>
          </div>
        </>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate {...turnstileActivationProps}>
        <div className="space-y-2">
          <label htmlFor="signup-name" className="text-sm font-medium leading-none">
            Имя
          </label>
          <Input
            id="signup-name"
            autoComplete="name"
            placeholder="Иван Иванов"
            data-testid="signup-name-input"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              clearFieldError('name');
            }}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'signup-name-error' : undefined}
          />
          <div id="signup-name-error">
            <FieldError message={errors.name} />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="signup-email" className="text-sm font-medium leading-none">
            Электронная почта
          </label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="ivan@example.com"
            data-testid="signup-email-input"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearFieldError('email');
            }}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'signup-email-error' : undefined}
          />
          <div id="signup-email-error">
            <FieldError message={errors.email} />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="signup-password" className="text-sm font-medium leading-none">
            Пароль
          </label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            data-testid="signup-password-input"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              clearFieldError('password');
              if (errors.confirmPassword && confirmPassword === event.target.value) {
                clearFieldError('confirmPassword');
              }
            }}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'signup-password-error' : undefined}
          />
          <div id="signup-password-error">
            <FieldError message={errors.password} />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="signup-confirm-password" className="text-sm font-medium leading-none">
            Подтвердите пароль
          </label>
          <Input
            id="signup-confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            data-testid="signup-confirm-password-input"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              clearFieldError('confirmPassword');
            }}
            aria-invalid={Boolean(errors.confirmPassword)}
            aria-describedby={errors.confirmPassword ? 'signup-confirm-password-error' : undefined}
          />
          <div id="signup-confirm-password-error">
            <FieldError message={errors.confirmPassword} />
          </div>
        </div>

        {turnstileEnabled ? (
          <div className="space-y-2">
            <p className="text-xs leading-5 text-[var(--site-ink-subtle)]">
              Антиспам-проверка подключится при взаимодействии с формой или перед отправкой. Если потребуется подтверждение, виджет появится здесь.
            </p>
            <div className="flex min-h-[1px] justify-end">
              {isTurnstileActivated ? (
                <DeferredTurnstile
                  sitekey={turnstileSiteKey as string}
                  size="flexible"
                  appearance={usesAlwaysPassKey ? 'always' : 'interaction-only'}
                  execution={usesAlwaysPassKey ? undefined : 'execute'}
                  onLoad={(_id, bound) => {
                    turnstileRef.current = bound;
                    if (captchaReadyWaiterRef.current) {
                      clearTimeout(captchaReadyWaiterRef.current.timeout);
                      captchaReadyWaiterRef.current.resolve(bound);
                      captchaReadyWaiterRef.current = null;
                    }
                  }}
                  onVerify={handleCaptchaVerify}
                  onError={handleCaptchaError}
                  onExpire={() => handleCaptchaError(new Error('Captcha expired'))}
                  onTimeout={() => handleCaptchaError(new Error('Captcha timed out'))}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="pt-2">
          <p className="mb-3 text-xs leading-5 text-[var(--site-ink-subtle)]">
            Нажимая «Создать аккаунт», вы соглашаетесь с{' '}
            <PublicLink href="/offer" className="text-[var(--site-accent-text)] hover:underline">
              Публичной офертой
            </PublicLink>
            ,{' '}
            <PublicLink href="/privacy" className="text-[var(--site-accent-text)] hover:underline">
              Политикой конфиденциальности
            </PublicLink>{' '}
            и даёте{' '}
            <PublicLink href="/personal-data-processing-consent" className="text-[var(--site-accent-text)] hover:underline">
              Согласие на обработку персональных данных
            </PublicLink>
            .
          </p>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            data-testid="signup-submit-button"
          >
            {isLoading ? 'Создаем аккаунт...' : 'Создать аккаунт по электронной почте'}
          </Button>
        </div>
      </form>

      <div className="text-center text-sm">
        <p className="text-[var(--site-ink-muted)]">
          Уже есть аккаунт?{' '}
          <PublicLink
            href={withCheckoutPlanLink('/login', checkoutPlan)}
            className="font-medium text-[var(--site-accent-text)] hover:underline"
          >
            Войти
          </PublicLink>
        </p>
        <p className="mt-3 text-[var(--site-ink-muted)]">
          Нужна помощь с выбором тарифа или завершением настройки?{' '}
          <PublicLink href="/help" className="font-medium text-[var(--site-accent-text)] hover:underline">
            Откройте центр помощи
          </PublicLink>
        </p>
      </div>
    </SurfacePanel>
  );
}
